// Hooks for store data from Supabase (public read; admin uses same client with RLS)
// Uses React Query for caching and loading states

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { getWorkerApi, postWorkerApi } from '@/lib/worker-api';
import type {
  Category,
  ProductFamily,
  SourcingItem,
  SourcingItemOrigin,
  Brand,
  Origin,
  OriginPort,
  LoadingMethod,
  MoqUnit,
  CostIncoterm,
  ProductStatus,
  Supplier,
  SupplierContact,
  Lead,
  QuotationRequest,
  QuotationRequestItem,
  QuotationRequestStatus,
  QuotationDocBlocks,
  QuotationDocument,
  QuotationHeaderImage,
  QuotationSealImage,
  CatalogueConversionDraft,
  CompanySettings,
  CompanyBankAccount,
  QuotationRequestDocument,
} from '@/lib/store-types';

const PUBLISHED_STATUS_NAME = 'Published';

// --- Categories ---
export function useCategories() {
  return useQuery({
    queryKey: ['store', 'categories'],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Product families (optionally by category) ---
// When categoryId is provided, fetch families for that category only.
// When categoryId is null/undefined we fetch ALL families (for search, etc.).
// To wait for a categoryId before fetching, pass options.enabled = false or pass undefined and check before calling.
export function useProductFamilies(categoryId?: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['store', 'product_families', categoryId ?? 'all'],
    enabled: options?.enabled !== false,
    queryFn: async (): Promise<ProductFamily[]> => {
      if (categoryId) {
        const { data: junction, error: je } = await supabase
          .from('product_family_categories')
          .select('family_id')
          .eq('category_id', categoryId);
        if (je) throw je;
        const familyIds = (junction ?? []).map((r) => r.family_id);
        if (familyIds.length === 0) return [];
        const { data, error } = await supabase
          .from('product_families')
          .select('*')
          .in('id', familyIds)
          .order('family_name');
        if (error) throw error;
        return data ?? [];
      }
      const { data, error } = await supabase
        .from('product_families')
        .select('*')
        .order('family_name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Sourcing items (optionally by family); public view omits cost/barcode ---
export function useSourcingItems(
  familyId?: string | null,
  options?: { admin?: boolean; enabled?: boolean }
) {
  const table = options?.admin ? 'sourcing_items' : 'public_sourcing_items';
  return useQuery({
    queryKey: ['store', 'sourcing_items', familyId ?? 'all', options?.admin],
    enabled: options?.enabled !== false,
    refetchOnMount: familyId ? 'always' : undefined,
    queryFn: async (): Promise<SourcingItem[]> => {
      if (familyId) {
        const { data: junction, error: je } = await supabase
          .from('sourcing_item_families')
          .select('item_id')
          .eq('family_id', familyId);
        if (je) throw je;
        const itemIds = (junction ?? []).map((r) => r.item_id);
        if (itemIds.length === 0) return [];
        const { data, error } = await supabase
          .from(table)
          .select('*')
          .in('id', itemIds)
          .order('internal_name');
        if (error) throw error;
        return data ?? [];
      }
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .order('internal_name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Item → Family → Category (for product breadcrumb) ---
export function useItemFamilyCategory(itemId: string | null) {
  return useQuery({
    queryKey: ['store', 'item_family_category', itemId ?? ''],
    queryFn: async (): Promise<{ familyId: string; categoryId: string } | null> => {
      if (!itemId) return null;
      const { data: itemFamily, error: e1 } = await supabase
        .from('sourcing_item_families')
        .select('family_id')
        .eq('item_id', itemId)
        .limit(1)
        .maybeSingle();
      if (e1 || !itemFamily?.family_id) return null;
      const { data: familyCat, error: e2 } = await supabase
        .from('product_family_categories')
        .select('category_id')
        .eq('family_id', itemFamily.family_id)
        .limit(1)
        .maybeSingle();
      if (e2 || !familyCat?.category_id) return null;
      return { familyId: itemFamily.family_id, categoryId: familyCat.category_id };
    },
    enabled: !!itemId,
  });
}

// --- Junctions (for search: item→family→category) ---
export function useSourcingItemFamilies() {
  return useQuery({
    queryKey: ['store', 'sourcing_item_families'],
    queryFn: async (): Promise<{ item_id: string; family_id: string }[]> => {
      const { data, error } = await supabase
        .from('sourcing_item_families')
        .select('item_id, family_id');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProductFamilyCategories() {
  return useQuery({
    queryKey: ['store', 'product_family_categories'],
    queryFn: async (): Promise<{ family_id: string; category_id: string }[]> => {
      const { data, error } = await supabase
        .from('product_family_categories')
        .select('family_id, category_id');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Mix options: which families can be mixed in a mix container. Used by admin, converter, suggestions, and StoreFamily sidebar. */
export function useProductFamilyMixOptions() {
  return useQuery({
    queryKey: ['store', 'product_family_mix_options'],
    queryFn: async (): Promise<{ family_id: string; related_family_id: string }[]> => {
      const { data, error } = await supabase
        .from('product_family_mix_options')
        .select('family_id, related_family_id');
      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Admin: attributes per family derived from its products (origin, supplier, brand from items).
 * One row per (family, item); use to filter families by origin/supplier/brand in admin.
 * See view admin_family_item_attributes.
 */
export function useFamilyItemAttributes(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['store', 'admin_family_item_attributes'],
    enabled: options?.enabled !== false,
    queryFn: async (): Promise<{ family_id: string; brand_id: string | null; origin_id: string | null; supplier_id: string | null }[]> => {
      const { data, error } = await supabase
        .from('admin_family_item_attributes')
        .select('family_id, brand_id, origin_id, supplier_id');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Lookups ---
export function useBrands() {
  return useQuery({
    queryKey: ['store', 'lookups', 'brands'],
    queryFn: async (): Promise<Brand[]> => {
      const { data, error } = await supabase.from('brands').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useOrigins() {
  return useQuery({
    queryKey: ['store', 'lookups', 'origins'],
    queryFn: async (): Promise<Origin[]> => {
      const { data, error } = await supabase.from('origins').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useOriginPorts(originId?: string | null) {
  return useQuery({
    queryKey: ['store', 'lookups', 'origin_ports', originId ?? 'all'],
    queryFn: async (): Promise<OriginPort[]> => {
      let q = supabase.from('origin_ports').select('*').order('name');
      if (originId) q = q.eq('origin_id', originId);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useLoadingMethods() {
  return useQuery({
    queryKey: ['store', 'lookups', 'loading_methods'],
    queryFn: async (): Promise<LoadingMethod[]> => {
      const { data, error } = await supabase.from('loading_methods').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSuppliers() {
  return useQuery({
    queryKey: ['store', 'lookups', 'suppliers'],
    queryFn: async (): Promise<Supplier[]> => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*, supplier_contacts(*)')
        .order('name');
      if (error) throw error;
      return (data ?? []) as Supplier[];
    },
  });
}

export function useMoqUnits() {
  return useQuery({
    queryKey: ['store', 'lookups', 'moq_units'],
    queryFn: async (): Promise<MoqUnit[]> => {
      const { data, error } = await supabase.from('moq_units').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCostIncoterms() {
  return useQuery({
    queryKey: ['store', 'lookups', 'cost_incoterms'],
    queryFn: async (): Promise<CostIncoterm[]> => {
      const { data, error } = await supabase.from('cost_incoterms').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useProductStatuses() {
  return useQuery({
    queryKey: ['store', 'lookups', 'product_statuses'],
    queryFn: async (): Promise<ProductStatus[]> => {
      const { data, error } = await supabase.from('product_statuses').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Sourcing Item Origins (admin) ---
// Fetch via worker API (same-origin). Direct Supabase from the browser often yields "Failed to load response data" and 4 retries; worker uses service role and returns JSON.
export function useSourcingItemOrigins(itemId?: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['store', 'sourcing_item_origins', itemId ?? ''],
    enabled: options?.enabled !== false && !!itemId,
    retry: false,
    queryFn: async (): Promise<SourcingItemOrigin[]> => {
      if (!itemId) return [];
      const result = await getWorkerApi<SourcingItemOrigin[]>(
        `/api/admin/sourcing-item-origins?itemId=${encodeURIComponent(itemId)}`
      );
      if (!result.ok) throw new Error(('error' in result ? result.error : undefined) || `HTTP ${result.status}`);
      return Array.isArray(result.data) ? result.data : [];
    },
  });
}

// Save or update an origin for a sourcing item
export function useSaveItemOrigin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (origin: Partial<SourcingItemOrigin> & { sourcing_item_id: string; origin_id: string }) => {
      if (origin.id) {
        // Update existing
        const { error } = await supabase
          .from('sourcing_item_origins')
          .update(origin)
          .eq('id', origin.id);
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('sourcing_item_origins')
          .insert(origin);
        if (error) throw error;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins', variables.sourcing_item_id] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
    },
  });
}

// Delete an origin from a sourcing item
export function useDeleteItemOrigin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, itemId }: { id: string; itemId: string }) => {
      const { error } = await supabase
        .from('sourcing_item_origins')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return itemId;
    },
    onSuccess: (itemId) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins', itemId] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
    },
  });
}

// Set an origin as active (will auto-deactivate others via DB trigger)
export function useSetActiveOrigin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ originId, itemId }: { originId: string; itemId: string }) => {
      const { error } = await supabase
        .from('sourcing_item_origins')
        .update({ is_active: true })
        .eq('id', originId);
      if (error) throw error;
      return itemId;
    },
    onSuccess: (itemId) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_item_origins', itemId] });
      queryClient.invalidateQueries({ queryKey: ['store', 'sourcing_items'] });
    },
  });
}

// --- Client (store): own lead + quotation requests ---
export function useClientLead() {
  return useQuery({
    queryKey: ['store', 'client', 'lead'],
    queryFn: async (): Promise<Lead | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

/** Quotation requests for the client's lead, with approved doc info. Includes status (draft, pending, finished). */
export function useClientQuotationRequests(leadId: string | null) {
  return useQuery({
    queryKey: ['store', 'client', 'quotation_requests', leadId],
    queryFn: async (): Promise<(QuotationRequest & { approved_doc?: { id: string; approved_pdf_url?: string; download_expires_at?: string } })[]> => {
      if (!leadId) return [];
      const { data: requests, error: reqErr } = await supabase
        .from('quotation_requests')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });
      if (reqErr) throw reqErr;
      const rows = requests ?? [];
      if (rows.length === 0) return [];
      const requestIds = rows.map((r) => r.id);
      const { data: docs, error: docErr } = await supabase
        .from('quotation_documents')
        .select('id, quotation_request_id, approved_at, approved_pdf_url, download_expires_at')
        .in('quotation_request_id', requestIds)
        .not('approved_at', 'is', null);
      if (docErr) throw docErr;
      const approvedByRequest = new Map((docs ?? []).map((d) => [d.quotation_request_id, d]));
      return rows.map((r) => {
        const approved = approvedByRequest.get(r.id);
        return {
          ...r,
          approved_doc: approved
            ? { id: approved.id, approved_pdf_url: approved.approved_pdf_url ?? undefined, download_expires_at: approved.download_expires_at ?? undefined }
            : undefined,
        };
      });
    },
    enabled: !!leadId,
  });
}

/** Client: set quotation_request status (e.g. pending → draft to re-edit and re-send). RLS allows update on own lead's requests. */
export function useUpdateQuotationRequestStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: QuotationRequestStatus }) => {
      const { data, error } = await supabase
        .from('quotation_requests')
        .update({ status })
        .eq('id', requestId)
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'client', 'quotation_requests'] });
      queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
    },
  });
}

/** Admin: patch quotation_request (status, lead_id). Invalidates admin list and detail. */
export function useAdminPatchQuotationRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      patch,
    }: {
      requestId: string;
      patch: {
        status?: QuotationRequestStatus;
        lead_id?: string | null;
        share_token?: string | null;
        items?: QuotationRequestItem[];
        blocks?: QuotationDocBlocks;
        incoterm?: string | null;
        destination_port_or_address?: string | null;
        addressee_company?: string | null;
        addressee_contact?: string | null;
        payment_type?: string | null;
        payment_method?: string | null;
        observaciones?: string | null;
        final_price_text?: string | null;
        document_title?: string | null;
        header_image_url?: string | null;
        seal_image_url?: string | null;
        final_message?: string | null;
        show_final_message?: boolean | null;
        bank_account_id?: string | null;
        etd_origin_port?: string | null;
        eta_destination_port?: string | null;
      };
    }) => {
      const { data, error } = await supabase
        .from('quotation_requests')
        .update(patch)
        .eq('id', requestId)
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { requestId }) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_requests'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_request', requestId] });
      queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Lead> & { id: string }) => {
      const { id, ...updates } = payload;
      const { data, error } = await supabase.from('leads').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'client', 'lead'] });
      queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'lead', variables.id] });
    },
  });
}

// --- Leads (admin) ---
export function useLeads() {
  return useQuery({
    queryKey: ['store', 'admin', 'leads'],
    queryFn: async (): Promise<Lead[]> => {
      const { data, error } = await supabase.from('leads').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Admin: single lead by id (for detail page). */
export function useLead(id: string | null) {
  return useQuery({
    queryKey: ['store', 'admin', 'lead', id],
    queryFn: async (): Promise<Lead | null> => {
      if (!id) return null;
      const { data, error } = await supabase.from('leads').select('*').eq('id', id).single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    },
    enabled: !!id,
  });
}

// --- Quotation requests (admin) ---
export function useQuotationRequests() {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_requests'],
    queryFn: async (): Promise<(QuotationRequest & { lead?: Lead })[]> => {
      const { data, error } = await supabase
        .from('quotation_requests')
        .select('*, lead:leads(*)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// --- Single quotation request (admin detail) ---
export function useQuotationRequest(id: string | null) {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_request', id],
    queryFn: async (): Promise<(QuotationRequest & { lead?: Lead }) | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('quotation_requests')
        .select('*, lead:leads(*)')
        .eq('id', id)
        .single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    },
    enabled: !!id,
  });
}

// --- Submit quotation: worker gets/creates lead and attaches to request ---
// Logged-in: pass request_id + lead_id + request data only. Anonymous: pass request_id + lead fields (name, email, ...).
export function useSubmitQuotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload:
        | {
            request_id: string;
            lead_id: string;
            specifications?: string;
            incoterm?: string;
            destination_port_or_address?: string;
          }
        | {
            request_id: string;
            name: string;
            email: string;
            phone?: string;
            whatsapp?: string;
            company?: string;
            country?: string;
            city?: string;
            specifications?: string;
            incoterm?: string;
            destination_port_or_address?: string;
          }
    ) => {
      const body: Record<string, unknown> = {
        request_id: payload.request_id,
        specifications: payload.specifications ?? null,
        incoterm: payload.incoterm ?? null,
        destination_port_or_address: payload.destination_port_or_address ?? null,
      };
      if ('lead_id' in payload && payload.lead_id) {
        body.lead_id = payload.lead_id;
      } else if ('email' in payload && payload.email) {
        body.name = payload.name;
        body.email = payload.email;
        body.phone = payload.phone ?? null;
        body.whatsapp = payload.whatsapp ?? null;
        body.company = payload.company ?? null;
        body.country = payload.country ?? null;
        body.city = payload.city ?? null;
      } else {
        throw new Error('lead_id or email required');
      }
      const res = await postWorkerApi('/api/quotation-request/submit', body);
      if (!res.ok) throw new Error((res as { error?: string }).error ?? 'Error al enviar');
      return { id: payload.request_id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotation-request-cart'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_requests'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'leads'] });
    },
  });
}

// === Quotation Documents (admin-generated catalogs/quotations) ===

// List all quotation documents (admin)
export function useQuotationDocuments() {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_documents'],
    queryFn: async (): Promise<QuotationDocument[]> => {
      const { data, error } = await supabase
        .from('quotation_documents')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// Get a single quotation document by id
export function useQuotationDocument(id: string | null) {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_document', id],
    queryFn: async (): Promise<QuotationDocument | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('quotation_documents')
        .select('*')
        .eq('id', id)
        .single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }
      return data;
    },
    enabled: !!id,
  });
}

// Create a new quotation document (returns the created record)
export function useCreateQuotationDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: Partial<QuotationDocument>) => {
      const { data, error } = await supabase
        .from('quotation_documents')
        .insert({
          title: doc.title ?? 'Cotización',
          header_image_url: doc.header_image_url ?? null,
          seal_image_url: doc.seal_image_url ?? null,
          final_message: doc.final_message ?? null,
          show_final_message: doc.show_final_message ?? false,
          blocks: doc.blocks ?? { general: { type: 'general', rows: [] }, mix: [] },
          addressee_company: doc.addressee_company ?? null,
          addressee_contact: doc.addressee_contact ?? null,
          incoterm: doc.incoterm ?? null,
          destination: doc.destination ?? null,
          final_price_text: doc.final_price_text ?? null,
          payment_type: doc.payment_type ?? null,
          payment_method: doc.payment_method ?? null,
          observaciones: doc.observaciones ?? null,
          quotation_request_id: doc.quotation_request_id ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as QuotationDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_documents'] });
    },
  });
}

// Update an existing quotation document
export function useUpdateQuotationDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<QuotationDocument> & { id: string }) => {
      const { data, error } = await supabase
        .from('quotation_documents')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as QuotationDocument;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_documents'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_document', variables.id] });
    },
  });
}

// Delete a quotation document
export function useDeleteQuotationDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('quotation_documents')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_documents'] });
    },
  });
}

// === Quotation Header Images (reusable gallery for PDF headers) ===

// List all saved header images
export function useQuotationHeaderImages() {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_header_images'],
    queryFn: async (): Promise<QuotationHeaderImage[]> => {
      const { data, error } = await supabase
        .from('quotation_header_images')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

// Save a new header image to the gallery
export function useCreateQuotationHeaderImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (img: { name: string; url: string }) => {
      const { data, error } = await supabase
        .from('quotation_header_images')
        .insert({ name: img.name, url: img.url })
        .select('*')
        .single();
      if (error) throw error;
      return data as QuotationHeaderImage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_header_images'] });
    },
  });
}

// Delete a header image from the gallery
export function useDeleteQuotationHeaderImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('quotation_header_images')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_header_images'] });
    },
  });
}

// === Quotation Seal Images (reusable gallery for PDF closing) ===

export function useQuotationSealImages() {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_seal_images'],
    queryFn: async (): Promise<QuotationSealImage[]> => {
      const { data, error } = await supabase
        .from('quotation_seal_images')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useCreateQuotationSealImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (img: { name: string; url: string }) => {
      const { data, error } = await supabase
        .from('quotation_seal_images')
        .insert({ name: img.name, url: img.url })
        .select('*')
        .single();
      if (error) throw error;
      return data as QuotationSealImage;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_seal_images'] });
    },
  });
}

export function useDeleteQuotationSealImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('quotation_seal_images')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'quotation_seal_images'] });
    },
  });
}

// --- Catalogue conversion drafts (converter wizard) ---
export function useCatalogueConversionDrafts() {
  return useQuery({
    queryKey: ['store', 'catalogue_conversion_drafts'],
    queryFn: async (): Promise<CatalogueConversionDraft[]> => {
      const { data, error } = await supabase
        .from('catalogue_conversion_drafts')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as CatalogueConversionDraft[];
    },
  });
}

export function useCatalogueConversionDraft(id: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['store', 'catalogue_conversion_drafts', id ?? ''],
    enabled: (options?.enabled !== false && !!id),
    queryFn: async (): Promise<CatalogueConversionDraft | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('catalogue_conversion_drafts')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data as CatalogueConversionDraft | null;
    },
  });
}

type DraftPatch = Partial<
  Pick<
    CatalogueConversionDraft,
    | 'step'
    | 'category_id'
    | 'supplier_id'
    | 'brand_id'
    | 'origin_id'
    | 'origin_port_id'
    | 'loading_method_id'
    | 'family_description'
    | 'user_prompt'
    | 'file_type'
    | 'file_url'
    | 'file_base64'
    | 'raw_blocks'
    | 'column_mapping'
    | 'converted_blocks'
    | 'match_decisions'
    | 'saved_family_ids'
    | 'saved_item_ids'
    | 'finalized_at'
  >
>;

export function useCatalogueConversionDraftMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: DraftPatch;
    }): Promise<CatalogueConversionDraft> => {
      const { data, error } = await supabase
        .from('catalogue_conversion_drafts')
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as CatalogueConversionDraft;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['store', 'catalogue_conversion_drafts'] });
      queryClient.invalidateQueries({ queryKey: ['store', 'catalogue_conversion_drafts', id] });
    },
  });
}

export function useCreateCatalogueConversionDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<CatalogueConversionDraft> => {
      const { data, error } = await supabase
        .from('catalogue_conversion_drafts')
        .insert({ step: 0 })
        .select('*')
        .single();
      if (error) throw error;
      return data as CatalogueConversionDraft;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'catalogue_conversion_drafts'] });
    },
  });
}

// === Company settings & bank accounts (admin) ===

export function useCompanySettings() {
  return useQuery({
    queryKey: ['store', 'admin', 'company_settings'],
    queryFn: async (): Promise<CompanySettings | null> => {
      const { data, error } = await supabase
        .from('company_settings')
        .select('*')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      // PGRST116 = no rows
      if (error && error.code !== 'PGRST116') throw error;
      return (data as CompanySettings | null) ?? null;
    },
  });
}

export function useUpsertCompanySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CompanySettings>) => {
      if (payload.id) {
        const { id, created_at, updated_at, ...rest } = payload;
        const { data, error } = await supabase
          .from('company_settings')
          .update({ ...rest, updated_at: new Date().toISOString() })
          .eq('id', id as string)
          .select('*')
          .single();
        if (error) throw error;
        return data as CompanySettings;
      }
      const { data, error } = await supabase
        .from('company_settings')
        .insert({
          legal_name: payload.legal_name ?? 'Padmora',
          entity_code: payload.entity_code ?? null,
          address: payload.address ?? null,
          header_image_url: payload.header_image_url ?? null,
          seal_image_url: payload.seal_image_url ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as CompanySettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'company_settings'] });
    },
  });
}

/** Admin: documents (PDFs, receipts) for a quotation request. */
export function useQuotationRequestDocuments(requestId: string | null) {
  return useQuery({
    queryKey: ['store', 'admin', 'quotation_request_documents', requestId],
    queryFn: async (): Promise<QuotationRequestDocument[]> => {
      if (!requestId) return [];
      const { data, error } = await supabase
        .from('quotation_request_documents')
        .select('*')
        .eq('quotation_request_id', requestId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as QuotationRequestDocument[] | null) ?? [];
    },
    enabled: !!requestId,
  });
}

export function useCompanyBankAccounts() {
  return useQuery({
    queryKey: ['store', 'admin', 'company_bank_accounts'],
    queryFn: async (): Promise<CompanyBankAccount[]> => {
      const { data, error } = await supabase
        .from('company_bank_accounts')
        .select('*')
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data as CompanyBankAccount[] | null) ?? [];
    },
  });
}

export function useUpsertCompanyBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CompanyBankAccount>) => {
      if (payload.id) {
        const { id, created_at, updated_at, ...rest } = payload;
        const { data, error } = await supabase
          .from('company_bank_accounts')
          .update({ ...rest, updated_at: new Date().toISOString() })
          .eq('id', id as string)
          .select('*')
          .single();
        if (error) throw error;
        return data as CompanyBankAccount;
      }
      const { data, error } = await supabase
        .from('company_bank_accounts')
        .insert({
          label: payload.label ?? 'Cuenta',
          bank_name: payload.bank_name ?? null,
          account_holder: payload.account_holder ?? null,
          account_number: payload.account_number ?? null,
          iban: payload.iban ?? null,
          swift: payload.swift ?? null,
          currency: payload.currency ?? null,
          account_type: payload.account_type ?? null,
          details: payload.details ?? null,
          beneficiary_bank_address: payload.beneficiary_bank_address ?? null,
          intermediary_bank_name: payload.intermediary_bank_name ?? null,
          intermediary_bank_address: payload.intermediary_bank_address ?? null,
          intermediary_bank_swift: payload.intermediary_bank_swift ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as CompanyBankAccount;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'company_bank_accounts'] });
    },
  });
}

export function useDeleteCompanyBankAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('company_bank_accounts').delete().eq('id', id);
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'admin', 'company_bank_accounts'] });
    },
  });
}

